import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Phone, Mail, MapPin, MessageCircle, Facebook, Twitter, Instagram, Youtube, ArrowRight } from 'lucide-react';
import { schoolInfo, menuItems } from '../utils/mock';

const Footer = () => {
  const handleWhatsAppClick = () => {
    window.open(schoolInfo.whatsappLink, '_blank');
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* School Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-white p-2 rounded-lg shadow-lg">
                <img 
                  src={schoolInfo.logo} 
                  alt="AMTEE PUBLIC SCHOOL Logo" 
                  className="h-12 w-12 object-contain"
                />
              </div>
              <div>
                <span className="font-extrabold text-lg text-white block">{schoolInfo.name}</span>
                <span className="text-xs text-yellow-400">Since 2017</span>
              </div>
            </div>
            <p className="text-sm text-gray-400 mb-4 leading-relaxed">
              Building Nation With Education
            </p>
            <p className="text-xs text-gray-500 mb-4">
              {schoolInfo.affiliation}
            </p>
            
            {/* Social Media Links */}
            <div className="flex gap-3 mt-6">
              <a 
                href="#" 
                className="bg-gray-800 hover:bg-blue-600 p-2.5 rounded-full transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                aria-label="Facebook"
              >
                <Facebook className="h-4 w-4 text-white" />
              </a>
              <a 
                href="#" 
                className="bg-gray-800 hover:bg-blue-400 p-2.5 rounded-full transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4 text-white" />
              </a>
              <a 
                href="#" 
                className="bg-gray-800 hover:bg-pink-600 p-2.5 rounded-full transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4 text-white" />
              </a>
              <a 
                href="#" 
                className="bg-gray-800 hover:bg-red-600 p-2.5 rounded-full transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
                aria-label="YouTube"
              >
                <Youtube className="h-4 w-4 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-bold text-lg text-white mb-6 relative inline-block">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-blue-500 rounded"></span>
            </h3>
            <ul className="space-y-3">
              {menuItems.map((item) => (
                <li key={item.name}>
                  <Link 
                    to={item.path} 
                    className="text-sm text-gray-400 hover:text-blue-400 transition-colors duration-200 flex items-center group"
                  >
                    <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-bold text-lg text-white mb-6 relative inline-block">
              Contact Us
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-blue-500 rounded"></span>
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3 text-sm text-gray-400 group">
                <MapPin className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5 group-hover:text-blue-300 transition-colors" />
                <span className="leading-relaxed">{schoolInfo.address}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-5 w-5 text-blue-400" />
                <a href={`tel:${schoolInfo.phone}`} className="text-gray-400 hover:text-blue-400 transition-colors">
                  {schoolInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-5 w-5 text-blue-400" />
                <a 
                  href={`mailto:${schoolInfo.email}?subject=School Inquiry`}
                  className="text-gray-400 hover:text-blue-400 transition-colors break-all"
                  aria-label="Send email to AMTEE PUBLIC SCHOOL"
                >
                  {schoolInfo.email}
                </a>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div>
            <h3 className="font-bold text-lg text-white mb-6 relative inline-block">
              Get In Touch
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-blue-500 rounded"></span>
            </h3>
            <p className="text-sm text-gray-400 mb-4 leading-relaxed">
              Have questions? We're here to help! Reach out to us anytime.
            </p>
            <Button 
              onClick={handleWhatsAppClick}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white w-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg shadow-md"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Chat on WhatsApp
            </Button>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="mt-3 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white w-full transition-all duration-300"
              >
                Send Message
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-800 my-8"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4 text-sm text-gray-400">
            <p>
              © {new Date().getFullYear()} <span className="text-white font-semibold">{schoolInfo.name}</span>. All rights reserved.
            </p>
            <span className="hidden md:inline text-gray-700">|</span>
            <p className="text-xs">
              Made with <span className="text-red-500">❤️</span> for Education
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Link 
              to="/teacher-login" 
              className="text-sm text-gray-500 hover:text-blue-400 transition-colors duration-200"
            >
              Teacher Login
            </Link>
            <span className="text-gray-700">|</span>
            <Link 
              to="/admin/login" 
              className="text-sm text-gray-500 hover:text-blue-400 transition-colors duration-200"
            >
              Admin Login
            </Link>
          </div>
        </div>
      </div>

      {/* Decorative gradient bar */}
      <div className="h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600"></div>
    </footer>
  );
};

export default Footer;